import React, { useState, useEffect } from "react";
import { useUser } from "../../UserContext";
import { doc, setDoc, updateDoc, getDoc } from "firebase/firestore";
import { db, applicantsCol } from "../../firebase";
import { universities } from "../../model/universities-model.jsx";

const maxChoices = 3; // Number of course choices allowed

const CourseSelection = () => {
  const { user, loading: userLoading } = useUser();
  const userID = user?.uid || "";
  const [selectedLevel, setSelectedLevel] = useState("");
  const [selectedUniversity, setSelectedUniversity] = useState("");
  const [courseChoices, setCourseChoices] = useState(Array(maxChoices).fill(""));
  const [isNewApplicant, setIsNewApplicant] = useState(false);
  const [issavingData, setIssavingData] = useState(false);
  const [disableInputFields, setDisableInputFields] = useState(false);
  const [message, setMessage] = useState("");
  const [isErrorOrSuccMsg, setIsErrorOrSuccMsg] = useState(false);
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [loading, setIsloading] = useState(true);
  const [hasSavedCourseSelection, setHasSavedCourseSelection] = useState(false);

  // Check if user has existing course selection data
  useEffect(() => {
    const checkExistingData = async () => {
      if (userID) {
        try {
          const docRef = doc(db, applicantsCol, userID);
          const applicantData = await getDoc(docRef);
          
          if (applicantData.exists()) {
            setIsNewApplicant(false);
            const data = applicantData.data();
            if (data.level) {
              setHasSavedCourseSelection(true);
              setSelectedLevel(data.level || "");
              setSelectedUniversity(data.university || "");
              setCourseChoices([
                data.firstChoice || "",
                data.secondChoice || "",
                data.thirdChoice || ""
              ]);
              setDisableInputFields(false); 
              console.log(selectedLevel);
              setIsloading(false);
              
              // Allow updates
            } else {
              setHasSavedCourseSelection(false);
            }
          } else {
            setIsNewApplicant(true);
          }
        } catch (error) {
          console.error("Error checking existing data:");
          setIsNewApplicant(false);
        }
      }
    };

    if (!userLoading && userID) {
      checkExistingData();
    }
  }, [userID, userLoading]);

  // Filter universities by selected level
  const filteredUniversities = selectedLevel
    ? universities.filter((uni) => uni.levels && uni.levels.includes(selectedLevel.toLowerCase()))
    : [];

  // Get courses for selected university
  const selectedUniObj = filteredUniversities.find(
    (uni) => uni.name === selectedUniversity
  );
  const availableCourses = selectedUniObj && selectedUniObj.courses && selectedUniObj.courses[selectedLevel.toLowerCase()]
    ? selectedUniObj.courses[selectedLevel.toLowerCase()].map(courseObj => courseObj.course)
    : [];

  // Helper function to get course details (duration, fee, etc.)
  const getCourseDetails = (courseName) => {
    if (!selectedUniObj || !selectedUniObj.courses || !selectedUniObj.courses[selectedLevel.toLowerCase()]) {
      return null;
    }
    return selectedUniObj.courses[selectedLevel.toLowerCase()].find(
      courseObj => courseObj.course === courseName
    );
  };

  // Handle course selection for each choice
  const handleCourseChoice = (index, value) => {
    const newChoices = [...courseChoices];
    newChoices[index] = value;
    setCourseChoices(newChoices);
  };

  // Prevent duplicate course selection
  const getAvailableForChoice = (index) => {
    return availableCourses.filter(
      (course) => !courseChoices.includes(course) || courseChoices[index] === course
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form data
    if (!selectedUniversity || !courseChoices.some(c => c)) {
      setIsErrorOrSuccMsg(true);
      setIsErrorMsg(true);
      setMessage("Please fill in all required fields");
      return;
    }

    setIssavingData(true);
    setIsErrorOrSuccMsg(false);
    
    try {
      // Get course details for each choice
      const firstChoiceDetails = courseChoices[0] ? getCourseDetails(courseChoices[0]) : null;
      const secondChoiceDetails = courseChoices[1] ? getCourseDetails(courseChoices[1]) : null;
      const thirdChoiceDetails = courseChoices[2] ? getCourseDetails(courseChoices[2]) : null;

      const formData = {
        university: selectedUniversity,
        firstChoice: courseChoices[0] || "",
        secondChoice: courseChoices[1] || "",
        thirdChoice: courseChoices[2] || "",
        // Save duration for each course choice
        firstChoiceDuration: firstChoiceDetails?.duration || "",
        secondChoiceDuration: secondChoiceDetails?.duration || "",
        thirdChoiceDuration: thirdChoiceDetails?.duration || "",
        courseSelectionSaved: true,
        level0: selectedLevel,
      };

      if (isNewApplicant) {
        await setDoc(doc(db, applicantsCol, userID), formData);
        setHasSavedCourseSelection(true);
        setMessage("Course selection saved successfully");
      } else {
        const applicantRef = doc(db, applicantsCol, userID);
        await updateDoc(applicantRef, formData);
        setMessage("Course selection updated successfully");
      }
      
      setIssavingData(false);
      setIsErrorOrSuccMsg(true);
      setIsErrorMsg(false);
      setDisableInputFields(false);
    } catch (error) {
      setIssavingData(false);
      setIsErrorOrSuccMsg(true);
      setIsErrorMsg(true);
      setMessage("Something went wrong, try again:");
    }
  };

  if (userLoading) {
    return <div className="loader"></div>;
  }

  if (!userID) {
    return <div>Please log in to access course selection.</div>;
  }

  return (
    <div className="form-container personal-info">
      <h2>Course Selection</h2>
      
      {isErrorOrSuccMsg && (
        <div
          className={`alert alert-${isErrorMsg ? "danger" : "success"}`}
          role="alert"
        >
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Level Selection */}
        <div style={{ marginBottom: 16 }}>
          <label>
            Level:
            <select
              value={selectedLevel}
              onChange={(e) => {
                setSelectedLevel(e.target.value);
                setSelectedUniversity("");
                setCourseChoices(Array(maxChoices).fill(""));
              }}
              style={{ marginLeft: 8 }}
              required
              disabled={true}
            >
              <option value="">{selectedLevel==="degree" ? 'Bachelor Degree' : 'Masters Degree'}</option>
              {selectedLevel === "degree" ? <option value="degree" selected>Bachelor Degree</option> : <option value="">--</option>}
              {selectedLevel === "masters" ? <option value="masters" selected>Masters Degree</option> : <option value="">--</option>}
            </select>
          </label>
        </div>

        {/* University Selection */}
        {selectedLevel && (
          <div style={{ marginBottom: 16 }}>
            <label>
              University:
              <select
                value={selectedUniversity}
                onChange={(e) => {
                  setSelectedUniversity(e.target.value);
                  setCourseChoices(Array(maxChoices).fill(""));
                }}
                style={{ marginLeft: 8 }}
                disabled={disableInputFields}
                required
              >
                <option value="">Select University</option>
                {filteredUniversities.map((uni) => (
                  <option key={uni.name} value={uni.name}>
                    {uni.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        {/* Course Choices */}
        {selectedUniversity && (
          <div>
            <h4>Available Courses</h4>
            {courseChoices.map((choice, idx) => (
              <div key={idx} style={{ marginBottom: 8 }}>
                <label>
                  {`${["First", "Second", "Third"][idx]} Choice:`}
                  <select
                    value={choice}
                    onChange={(e) => handleCourseChoice(idx, e.target.value)}
                    style={{ marginLeft: 8 }}
                    disabled={disableInputFields}
                    required={idx === 0} // Only first choice is required
                  >
                    <option value="">Select Course</option>
                    {getAvailableForChoice(idx).map((course) => {
                      const courseDetails = getCourseDetails(course);
                      const durationText = courseDetails && courseDetails.duration ? ` (${courseDetails.duration})` : '';
                      return (
                        <option key={course} value={course}>
                          {course}{durationText}
                        </option>
                      );
                    })}
                  </select>
                </label>
              </div>
            ))}
          </div>
        )}

        {/* Summary */}
        {selectedUniversity && courseChoices.some((c) => c) && (
          <div style={{ marginTop: 24, background: "#f9f9f9", padding: 12, borderRadius: 6, color: "black" }}>
            <h4>Summary</h4>
            <div>Level: <b>{selectedLevel}</b></div>
            <div>University: <b>{selectedUniversity}</b></div>
            <div>
              Choices:
              <ol>
                {courseChoices.map((c, i) => {
                  if (!c) return null;
                  const courseDetails = getCourseDetails(c);
                  return (
                    <li key={i}>
                      {["First", "Second", "Third"][i]}: {c}
                      {courseDetails && courseDetails.duration && (
                        <span style={{ color: "#666", fontSize: "0.9em", marginLeft: "8px" }}>
                          (Duration: {courseDetails.duration})
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div style={{ marginTop: 24, textAlign: "center" }}>
          <button
            type="submit"
            disabled={
              issavingData ||
              !selectedLevel ||
              !selectedUniversity ||
              !courseChoices.some((c) => c)
            }
            style={{
              padding: "10px 24px",
              background: hasSavedCourseSelection ? "#28a745" : "#007bff",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              cursor: issavingData || !selectedLevel || !selectedUniversity || !courseChoices.some((c) => c) ? "not-allowed" : "pointer",
              opacity: issavingData || !selectedLevel || !selectedUniversity || !courseChoices.some((c) => c) ? 0.6 : 1,
            }}
          >
            {issavingData ? "Saving..." : hasSavedCourseSelection ? "Update Selection" : "Save Selection"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CourseSelection;
